import React from 'react';
import { Check, X, Loader2 } from 'lucide-react';
import { TIERS } from '../data/mockData';
import { toast } from 'sonner';
import { startCheckout } from '../lib/payments';

const COMPARE_ROWS = [
  { label: 'Browse marketplace', values: [true, true, true, true] },
  { label: 'Vehicle listings', values: ['0', '3', '10', 'Unlimited'] },
  { label: 'Ride share requests / month', values: ['3', 'Unlimited', 'Unlimited', 'Unlimited'] },
  { label: 'Sales commission', values: ['5%', '4.5%', '3%', '1.5%'] },
  { label: 'Host private events', values: [false, false, true, true] },
  { label: 'Verified badge', values: [false, false, 'Enthusiast', 'Dealer'] },
  { label: 'Analytics dashboard', values: [false, false, true, true] },
  { label: 'Bulk import (CSV)', values: [false, false, false, true] },
  { label: 'Lead CRM & inbox', values: [false, false, false, true] },
  { label: 'API access', values: [false, false, false, true] },
];

function Cell({ v }) {
  if (v === true) return <Check className="w-4 h-4 text-[#ff2a2a] mx-auto"/>;
  if (v === false) return <X className="w-4 h-4 text-zinc-700 mx-auto"/>;
  return <span className="font-mono text-xs text-white">{v}</span>;
}

export default function Pricing() {
  const [billed, setBilled] = React.useState('monthly');
  const [loadingTier, setLoadingTier] = React.useState(null);
  const mult = billed === 'annual' ? 10 : 1;
  const label = billed === 'annual' ? '/yr' : '/mo';

  const handleSelect = async (t) => {
    if (!t.lookup_keys) {
      toast.success("You're on the Free tier. Welcome to the crew!");
      return;
    }
    const lookupKey = t.lookup_keys[billed];
    if (!lookupKey) {
      toast.error('Selected plan is unavailable.');
      return;
    }
    try {
      setLoadingTier(t.id);
      const { checkout_url } = await startCheckout(lookupKey);
      if (checkout_url) {
        window.location.href = checkout_url;
      } else {
        toast.error('Could not start checkout.');
        setLoadingTier(null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Checkout failed. Please try again.');
      setLoadingTier(null);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-10 py-14" data-testid="page-pricing">
      <div className="text-center max-w-2xl mx-auto">
        <div className="font-mono text-xs uppercase tracking-widest text-[#ff2a2a] mb-2">Pricing</div>
        <h1 className="font-display text-5xl sm:text-6xl uppercase tracking-tight leading-none">Pick your gear</h1>
        <p className="mt-4 text-zinc-400">Free forever tier. Upgrade for lower commissions, more listings, and premium community access.</p>

        <div className="mt-8 inline-flex hairline p-1" data-testid="billing-toggle">
          {['monthly', 'annual'].map((b) => (
            <button
              key={b}
              data-testid={`billing-${b}`}
              onClick={() => setBilled(b)}
              className={`px-4 py-2 text-xs font-mono uppercase tracking-widest transition-colors ${
                billed === b ? 'bg-[#ff2a2a] text-white' : 'text-zinc-400 hover:text-white'
              }`}
            >
              {b}{b === 'annual' && <span className="ml-2 text-[9px] opacity-70">Save ~17%</span>}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="tiers-grid">
        {TIERS.map((t) => (
          <div key={t.id} className={`am-card p-6 relative flex flex-col ${t.highlight ? 'border-[#ff2a2a]' : ''}`} data-testid={`tier-${t.id}`}>
            {t.highlight && (
              <div className="absolute -top-3 left-6 bg-[#ff2a2a] text-white text-[10px] font-mono uppercase tracking-widest px-2 py-1">
                Most Popular
              </div>
            )}
            <div className="font-mono text-xs uppercase tracking-widest text-zinc-500">{t.name}</div>
            <div className="mt-3 flex items-baseline gap-1">
              <div className="font-display text-5xl text-white">${(t.price * mult).toFixed(t.price === 0 ? 0 : 2)}</div>
              <div className="text-xs text-zinc-500">{t.price === 0 ? '/forever' : label}</div>
            </div>
            <div className="text-xs text-zinc-400 mt-1">{t.tagline}</div>
            <ul className="mt-5 space-y-2 flex-1">
              {t.features.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                  <Check className="w-4 h-4 text-[#ff2a2a] mt-0.5 shrink-0"/>{f}
                </li>
              ))}
            </ul>
            <button
              onClick={() => handleSelect(t)}
              disabled={loadingTier === t.id}
              className={`mt-6 py-2.5 text-sm font-semibold transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed ${
                t.highlight ? 'bg-[#ff2a2a] text-white hover:bg-[#e01f1f]' : 'hairline text-white hover:border-[#ff2a2a]'
              }`}
              data-testid={`tier-select-${t.id}`}
            >
              {loadingTier === t.id && <Loader2 className="w-4 h-4 animate-spin" />}
              {loadingTier === t.id ? 'Redirecting…' : t.cta}
            </button>
          </div>
        ))}
      </div>

      {/* Comparison table */}
      <div className="mt-20" data-testid="compare-table">
        <h2 className="font-display text-3xl uppercase tracking-tight mb-6">Compare plans</h2>
        <div className="am-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="hairline-b">
                <th className="text-left font-mono uppercase tracking-widest text-xs text-zinc-500 p-4">Feature</th>
                {TIERS.map((t) => (
                  <th key={t.id} className="font-mono uppercase tracking-widest text-xs text-white p-4 text-center">{t.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMPARE_ROWS.map((r, i) => (
                <tr key={i} className="hairline-b last:border-b-0">
                  <td className="p-4 text-zinc-300">{r.label}</td>
                  {r.values.map((v, j) => (
                    <td key={j} className="p-4 text-center"><Cell v={v}/></td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
